class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    // Prevent body scroll
    document.body.classList.add('overflow-hidden');
    // Store current scroll position
    this.scrollY = window.scrollY;
    document.body.style.top = `-${this.scrollY}px`;
    
    // Prevent scroll events on body/background, but allow scrolling inside drawer
    this.preventBodyScroll = (e) => {
      // Only prevent if the event target is outside the cart drawer
      const target = e.target;
      const isInsideDrawer = this.contains(target) || target.closest('cart-drawer');
      if (!isInsideDrawer) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
    document.addEventListener('wheel', this.preventBodyScroll, { passive: false });
    document.addEventListener('touchmove', this.preventBodyScroll, { passive: false });
    document.addEventListener('scroll', this.preventBodyScroll, { passive: false, capture: true });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    
    // Remove scroll event listeners first
    if (this.preventBodyScroll) {
      document.removeEventListener('wheel', this.preventBodyScroll);
      document.removeEventListener('touchmove', this.preventBodyScroll);
      document.removeEventListener('scroll', this.preventBodyScroll, { capture: true });
      this.preventBodyScroll = null;
    }
    
    // Restore body scroll - remove all inline styles and classes
    const scrollY = this.scrollY !== undefined ? this.scrollY : (document.documentElement.scrollTop || window.pageYOffset || 0);
    
    // Force remove overflow-hidden class
    document.body.classList.remove('overflow-hidden');
    
    // Clear all inline styles that might prevent scrolling
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('height');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    document.body.style.removeProperty('overflow');
    
    // Force enable scrolling by setting styles directly
    requestAnimationFrame(() => {
      // Ensure body can scroll
      document.body.style.setProperty('overflow', 'auto', 'important');
      document.body.style.setProperty('position', 'relative', 'important');
      document.body.style.setProperty('width', 'auto', 'important');
      document.body.style.setProperty('height', 'auto', 'important');
      
      // Restore scroll position
      window.scrollTo({
        top: scrollY,
        behavior: 'instant'
      });
      // Also set scrollTop directly as fallback
      document.documentElement.scrollTop = scrollY;
      document.body.scrollTop = scrollY;
      
      // Double check after a brief delay
      setTimeout(() => {
        if (document.body.classList.contains('overflow-hidden')) {
          document.body.classList.remove('overflow-hidden');
        }
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('width');
        document.body.style.removeProperty('height');
      }, 100);
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Only open cart drawer on product pages
    const isProductPage = window.location.pathname.includes('/products/');
    if (isProductPage) {
      setTimeout(() => {
        // Disabled auto-close on overlay click - only close via close button or Escape key
        // this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
